import React, { useState } from "react";
import { Copy, Check } from "lucide-react";

/**
 * Phase 3I.4R.2 — Tiny premium copy-to-clipboard icon.
 *
 * On click: writes `value` to the clipboard, briefly flips to a green
 * check + "Copied" state for ~1.6s, then reverts. `stopPropagation`
 * prevents the parent row/link from firing (e.g. inside a clickable
 * table row).
 */
export default function CopyButton({ value, label = "Copy", testId, className = "" }) {
  const [copied, setCopied] = useState(false);

  const onClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
    } catch (_) {
      // Fallback: transient textarea
      const ta = document.createElement("textarea");
      ta.value = String(value);
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute"; ta.style.left = "-9999px";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch (_) {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  if (!value) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label={`${label} ${value}`}
      title={copied ? "Copied" : label}
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5
                  text-[10px] tracking-[0.14em] uppercase font-black
                  border border-[#EAEAEA] bg-white
                  ${copied ? "text-[#0F7B3E] border-[#B7EBCF] bg-[#EBFBF2]"
                           : "text-[#8A8A8A] hover:text-[#0A0A0A] hover:border-[#0A0A0A]"}
                  transition-colors ${className}`}
    >
      {copied ? <Check size={11}/> : <Copy size={11}/>}
      <span className="hidden md:inline">{copied ? "Copied" : label}</span>
    </button>
  );
}
